import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  Typography,
  CardActions,
  IconButton,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Menu,
  MenuItem,
  CardMedia,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  Button,
  Box,
  LinearProgress,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import CloseIcon from '@mui/icons-material/Close';
import ReplayIcon from '@mui/icons-material/Replay';
import PublishIcon from '@mui/icons-material/Publish';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PendingIcon from '@mui/icons-material/Pending';

import { styled } from '@mui/styles';
import { useTranslation } from './LocalizationProvider';
import RemoveDialog from './RemoveDialog';
import PositionValue from './PositionValue';
import { useAdministrator, useDeviceReadonly } from '../util/permissions';
import usePositionAttributes from '../attributes/usePositionAttributes';
import { devicesActions } from '../../store';
import { useCatch, useCatchCallback } from '../../reactHelper';
import { useAttributePreference } from '../util/preferences';
import useFeatures from '../util/useFeatures';
import SelectField from './SelectField';

const useStyles = makeStyles((theme) => ({
  card: {
    pointerEvents: 'auto',
    width: theme.dimensions.popupMaxWidth,
  },
  media: {
    height: theme.dimensions.popupImageHeight,
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'flex-start',
  },
  mediaButton: {
    color: theme.palette.colors.white,
    mixBlendMode: 'difference',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(1, 1, 0, 2),
  },
  content: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },
  negative: {
    color: theme.palette.colors.negative,
  },
  icon: {
    width: '25px',
    height: '25px',
    filter: 'brightness(0) invert(1)',
  },
  table: {
    '& .MuiTableCell-sizeSmall': {
      paddingLeft: 0,
      paddingRight: 0,
    },
  },
  cell: {
    borderBottom: 'none',
  },
  actions: {
    justifyContent: 'space-between',
  },
  root: ({ desktopPadding }) => ({
    pointerEvents: 'none',
    position: 'fixed',
    zIndex: 5,
    left: '50%',
    [theme.breakpoints.up('md')]: {
      left: `calc(50% + ${desktopPadding} / 2)`,
      bottom: theme.spacing(3),
    },
    [theme.breakpoints.down('md')]: {
      left: '50%',
      bottom: `calc(${theme.spacing(3)} + ${theme.dimensions.bottomBarHeight}px)`,
    },
    transform: 'translateX(-50%)',
  }),
}));
const MaintenanceSwitch = styled(Switch)(({ theme }) => ({
  height: 20,
  padding: 0,
  overflow: 'visible',
  '& .MuiSwitch-switchBase': {
    margin: 0,
    padding: 0,
    transform: 'translateX(0px) translateY(-6px)',
    '&.Mui-checked': {
      color: '#fff',
      transform: 'translateX(26px) translateY(-6px)',
      '& .MuiSwitch-thumb:before': {
        backgroundColor: theme.palette.primary.main,
        borderRadius: '50%',
        width: '100%',
        height: '100%',
        backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="24" width="24" viewBox="0 0 32 32"><path fill="${encodeURIComponent(
          '#fff',
        )}" d="M13.778 6.667v2.222h3.333v2.222h2.222v-2.222h3.333v-2.222h-8.889zM13.778 13.333c-0.3 0-0.579 0.121-0.79 0.321l-1.888 1.901h-2.878c-0.611 0-1.111 0.5-1.111 1.111v6.667c0 0.611 0.5 1.111 1.111 1.111h1.888l3.056 2.033c0.178 0.122 0.39 0.189 0.612 0.189h5.556c0.244 0 0.477-0.077 0.666-0.221l4.444-3.333c0.278-0.211 0.445-0.545 0.445-0.89v-7.778c0-0.611-0.5-1.111-1.111-1.111h-10zM27.111 14.444v6.667h2.222v-6.667h-2.222zM17.111 15.556v3.333h2.222l-3.333 5.556v-3.333h-2.222l3.333-5.556zM2.667 16.667v5.556h2.222v-5.556h-2.222z"/></svg>')`,
      },
      '& + .MuiSwitch-track': {
        opacity: 1,
        backgroundColor: theme.palette.primary.dark,
      },
    },
  },
  '& .MuiSwitch-thumb': {
    backgroundColor: theme.palette.colors.white,
    borderRadius: '50%',
    width: 32,
    height: 32,
    '&::before': {
      content: "''",
      position: 'absolute',
      width: '100%',
      height: '100%',
      left: 0,
      top: 0,
      backgroundColor: theme.palette.error.main,
      borderRadius: '50%',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center',
      backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="24" width="24" viewBox="0 0 24 24"><path fill="${encodeURIComponent(
        '#fff',
      )}" d="m22.7 19-9.1-9.1c.9-2.3.4-5-1.5-6.9-2-2-5-2.4-7.4-1.3L9 6 6 9 1.6 4.7C.4 7.1.9 10.1 2.9 12.1c1.9 1.9 4.6 2.4 6.9 1.5l9.1 9.1c.4.4 1 .4 1.4 0l2.3-2.3c.5-.4.5-1.1.1-1.4"/></svg>')`,
    },
  },
  '& .MuiSwitch-track': {
    opacity: 1,
    backgroundColor: theme.palette.error.dark,
    borderRadius: 20 / 2,
  },
}));
const StatusRow = ({ name, content, nameSpan = 1, contentSpan = 1 }) => {
  const classes = useStyles();

  return (
    <TableRow>
      {name && (
        <TableCell className={classes.cell} colSpan={nameSpan}>
          <Typography variant="body2">{name}</Typography>
        </TableCell>
      )}
      {content && (
        <TableCell className={classes.cell} colSpan={contentSpan}>
          <Typography variant="body2" color="textSecondary">{content}</Typography>
        </TableCell>
      )}
    </TableRow>
  );
};
const ConfirmationDialog = ({ open, setOpen, handleClose, confirmCallback }) => {
  const t = useTranslation();
  const [loading, setLoading] = useState(false);
  const [text, setText] = useState('');
  const maxLenght = 50;

  const handleConfirm = async () => {
    setLoading(true);
    await confirmCallback({ maintenance: true, details: text });
    setOpen(false);
    setText('');
    setLoading(false);
  };
  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="xs"
    >
      <DialogTitle>{t('sharedMaintenanceDetails')}</DialogTitle>
      <DialogContent>

        <TextField
          autoFocus
          required
          margin="dense"
          id="name"
          fullWidth
          variant="standard"
          multiline
          value={text}
          onChange={(e) => setText(e.target.value)}
          helperText={`(${text.length} /${maxLenght})`}
          inputProps={{ maxLength: maxLenght }}
        />
      </DialogContent>
      <DialogActions>
        {
          !loading ?
            (
              <>
                <Button onClick={handleClose}>{t('sharedCancel')}</Button>
                <Button onClick={handleConfirm}>{t('sharedSave')}</Button>
              </>
            )
            :
            (
              <Box sx={{ width: '100%' }}>
                <LinearProgress />
              </Box>
            )
        }

      </DialogActions>
    </Dialog>
  );
};

const StatusCard = ({ deviceId, position, onClose, disableActions, desktopPadding = 0 }) => {
  const classes = useStyles({ desktopPadding });
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const t = useTranslation();

  const deviceReadonly = useDeviceReadonly();
  const admin = useAdministrator();
  const features = useFeatures();

  const device = useSelector((state) => state.devices.items[deviceId]);
  const deviceImage = device?.attributes?.deviceImage;

  const positionAttributes = usePositionAttributes(t);
  const positionItems = useAttributePreference('positionItems', 'speed,address,totalDistance,course');

  const [anchorEl, setAnchorEl] = useState(null);

  const [removing, setRemoving] = useState(false);

  const [confirm, setConfirm] = useState(false);

  const handleRemove = useCatch(async (removed) => {
    if (removed) {
      const response = await fetch('/api/devices');
      if (response.ok) {
        dispatch(devicesActions.refresh(await response.json()));
      } else {
        throw Error(await response.text());
      }
    }
    setRemoving(false);
  });

  const handleGeofence = useCatchCallback(async () => {
    const newItem = {
      name: '',
      area: `CIRCLE (${position.latitude} ${position.longitude}, 50)`,
    };
    const response = await fetch('/api/geofences', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(newItem),
    });
    if (response.ok) {
      const item = await response.json();
      const permissionResponse = await fetch('/api/permissions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ deviceId: position.deviceId, geofenceId: item.id }),
      });
      if (!permissionResponse.ok) {
        throw Error(await permissionResponse.text());
      }
      navigate(`/settings/geofence/${item.id}`);
    } else {
      throw Error(await response.text());
    }
  }, [navigate, position]);

  const handleMaintenanceUpdate = async ({ maintenance, details }) => {
    const response = await fetch(`/api/devices/${deviceId}`);
    if (response.ok) {
      const currentInfo = await response.json();
      let operativeDate = currentInfo.attributes.operativeDate ? new Date(currentInfo.attributes.operativeDate) : new Date();
      operativeDate.setMinutes(operativeDate.getMinutes() + operativeDate.getTimezoneOffset());
      let maintenanceDate = currentInfo.attributes.operativeDate ? new Date(currentInfo.attributes.maintenanceDate) : new Date();
      maintenanceDate.setMinutes(operativeDate.getMinutes() + operativeDate.getTimezoneOffset());
      const changeDate = new Date();
      changeDate.setHours(0, 0, 0, 0);
      if (maintenance && (changeDate > operativeDate)) {
        maintenanceDate = changeDate;
      } else if (changeDate > maintenanceDate) {
        operativeDate = changeDate;
      }
      const newInfo = {
        ...currentInfo,
        attributes:
        {
          ...currentInfo.attributes,
          maintenance,
          maintenanceDetails: details || '',
          maintenanceDate: maintenanceDate.toISOString().split('T')[0],
          operativeDate: operativeDate.toISOString().split('T')[0],
        },
      };
      const updateResponse = await fetch(`/api/devices/${deviceId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newInfo),
      });
      if (updateResponse.ok) {
        dispatch(devicesActions.updateItem(await updateResponse.json()));
      } else {
        throw Error(await updateResponse.text());
      }
    } else {
      throw Error(await response.text());
    }
  };

  const handleMaintananceChange = async (e) => {
    const maintenance = !e.target.checked;
    if (maintenance) {
      setConfirm(true);
      return;
    }

    handleMaintenanceUpdate({ maintenance });
  };

  const handleUpdateGroup = async (groupId) => {
    const response = await fetch(`/api/devices/${deviceId}`);
    if (response.ok) {
      const currentInfo = await response.json();
      const newInfo = {
        ...currentInfo,
        groupId,
      };
      const updateResponse = await fetch(`/api/devices/${deviceId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newInfo),
      });
      if (updateResponse.ok) {
        dispatch(devicesActions.updateItem(await updateResponse.json()));
      } else {
        throw Error(await updateResponse.text());
      }
    } else {
      throw Error(await response.text());
    }
  };

  return (
    <>
      <div className={classes.root}>
        {device && (
          <Card elevation={3} className={classes.card}>
            {deviceImage && (
              <CardMedia
                className={classes.media}
                image={`/api/media/${device.uniqueId}/${deviceImage}`}
              >
                <IconButton
                  size="small"
                  onClick={onClose}
                  onTouchStart={onClose}
                >
                  <CloseIcon fontSize="small" className={classes.mediaButton} />
                </IconButton>
              </CardMedia>
            )}
            <div className={classes.header}>
              <Typography variant="body2" color="textSecondary">
                {device.name}
              </Typography>
              {
                !deviceImage && (
                  <IconButton
                    size="small"
                    onClick={onClose}
                    onTouchStart={onClose}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )
              }

            </div>

            {position && (
              <CardContent className={classes.content}>
                <Table size="small" classes={{ root: classes.table }}>
                  <TableBody>
                    {positionItems.split(',').filter((key) => position.hasOwnProperty(key) || position.attributes.hasOwnProperty(key)).map((key) => (
                      <StatusRow
                        key={key}
                        name={positionAttributes.hasOwnProperty(key) ? positionAttributes[key].name : key}
                        content={(
                          <PositionValue
                            position={position}
                            property={position.hasOwnProperty(key) ? key : null}
                            attribute={position.hasOwnProperty(key) ? null : key}
                          />
                        )}
                      />
                    ))}
                    <StatusRow
                      name={(
                        <MaintenanceSwitch
                          checked={!device.attributes.maintenance}
                          onChange={handleMaintananceChange}
                          disabled={disableActions || deviceReadonly}
                        />
                      )}
                      content={
                        device.attributes.maintenance ?
                          t('maintenanceStatusMaintenance')
                          :
                          t('maintenanceStatusOperative')
                      }
                    />
                    {device.attributes.maintenance && (
                      <StatusRow
                        name={t('sharedMaintenanceDetails')}
                        content={device.attributes.maintenanceDetails || ''}
                      />
                    )}

                  </TableBody>
                </Table>
                <SelectField
                  label={t('groupParent')}
                  value={device.groupId || 0}
                  onChange={(e) => handleUpdateGroup(e.target.value)}
                  endpoint="/api/groups"
                  filterKey="attributes.type"
                  filterValue="construction"
                  disabled={disableActions || deviceReadonly}
                  sx={{ width: '100%', maxWidth: '100%', mt: 2 }}
                />
              </CardContent>
            )}
            <CardActions classes={{ root: classes.actions }} disableSpacing>
              <IconButton
                color="secondary"
                onClick={(e) => setAnchorEl(e.currentTarget)}
                disabled={!position}
              >
                <PendingIcon />
              </IconButton>
              <IconButton
                onClick={() => navigate('/replay')}
                disabled={disableActions || !position}
              >
                <ReplayIcon />
              </IconButton>
              <IconButton
                onClick={() => navigate(`/settings/command-send/${deviceId}`)}
                disabled={disableActions}
              >
                <PublishIcon />
              </IconButton>
              <IconButton
                onClick={() => navigate(`/settings/device/${deviceId}`)}
                disabled={disableActions || deviceReadonly || features.disableDeviceEditInfo}
              >
                <EditIcon />
              </IconButton>
              <IconButton
                onClick={() => setRemoving(true)}
                disabled={disableActions || deviceReadonly || !admin}
                className={classes.negative}
              >
                <DeleteIcon />
              </IconButton>
            </CardActions>
          </Card>
        )}
      </div>
      {position && (
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
          <MenuItem onClick={() => navigate(`/position/${position.id}`)}><Typography color="secondary">{t('sharedShowDetails')}</Typography></MenuItem>
          <MenuItem onClick={handleGeofence}>{t('sharedCreateGeofence')}</MenuItem>
          <MenuItem component="a" target="_blank" href={`https://www.google.com/maps/search/?api=1&query=${position.latitude}%2C${position.longitude}`}>{t('linkGoogleMaps')}</MenuItem>
          <MenuItem component="a" target="_blank" href={`http://maps.apple.com/?ll=${position.latitude},${position.longitude}`}>{t('linkAppleMaps')}</MenuItem>
          <MenuItem component="a" target="_blank" href={`https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${position.latitude}%2C${position.longitude}&heading=${position.course}`}>{t('linkStreetView')}</MenuItem>
        </Menu>
      )}
      <RemoveDialog
        open={removing}
        endpoint="devices"
        itemId={deviceId}
        onResult={(removed) => handleRemove(removed)}
      />
      <ConfirmationDialog
        open={confirm}
        setOpen={setConfirm}
        handleClose={() => setConfirm(false)}
        confirmCallback={handleMaintenanceUpdate}
      />
    </>
  );
};

export default StatusCard;
